import api from '@lib/api'
import { getErrorMessage } from '@lib/apiError'
import { queryKeys } from '@lib/queryKeys'
import { GetEventResponse, GetEventsResponse } from '@orbit/api-types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import toast from 'react-hot-toast'
import { applyPlacement, PlacementPatch } from './core/placement'

const PLACE_EVENT_KEY = ['placeEvent']

/**
 * Event placement, committed optimistically (ADR 0009): the webapp's one
 * mutation that does not wait for the server before the screen changes. The
 * cached events take the new order on mutate, lose only the failed move on
 * error — placements made since and still on their way are kept — and are
 * refetched once the last placement in flight has settled: a refetch between
 * two drags would answer without the second and pull its card back. `onSettled`
 * tells the caller its move is done, either way. A one-off beside
 * `createTripMutation`, deliberately not inside it.
 */
export const usePlaceEvent = (tripId: string): ((patch: PlacementPatch, onSettled?: () => void) => void) => {
	const queryClient = useQueryClient()
	const eventsKey = queryKeys.events(tripId)

	const { mutate } = useMutation<GetEventResponse, Error, PlacementPatch, { previous?: GetEventsResponse }>({
		mutationKey: PLACE_EVENT_KEY,
		mutationFn: async ({ eventId, tripDayIndex, eventIndex }) => {
			const response = await api.patch<GetEventResponse>(`/trips/${tripId}/events/${eventId}`, {
				tripDayIndex,
				eventIndex,
			})
			return response.data
		},
		onMutate: async patch => {
			// A refetch already on its way would land on top of the optimistic order.
			await queryClient.cancelQueries({ queryKey: eventsKey })
			const previous = queryClient.getQueryData<GetEventsResponse>(eventsKey)
			if (previous) {
				queryClient.setQueryData<GetEventsResponse>(eventsKey, {
					...previous,
					events: applyPlacement(previous.events, patch),
				})
			}
			return { previous }
		},
		onError: (error, patch, context) => {
			console.error('Failed to move event:', error)
			if (context?.previous) {
				// Back to before this move, then forward again through the moves made after it.
				const stillPending = queryClient
					.getMutationCache()
					.findAll({ mutationKey: PLACE_EVENT_KEY, status: 'pending' })
					.map(mutation => mutation.state.variables as PlacementPatch)
					.filter(pending => pending !== patch)
				queryClient.setQueryData<GetEventsResponse>(eventsKey, {
					...context.previous,
					events: stillPending.reduce(applyPlacement, context.previous.events),
				})
			}
			toast.error(getErrorMessage(error, 'Failed to move event. Please try again.'))
		},
		onSettled: () => {
			// This one still counts as mutating while its callbacks run.
			if (queryClient.isMutating({ mutationKey: PLACE_EVENT_KEY }) === 1) {
				void queryClient.invalidateQueries({ queryKey: eventsKey })
			}
		},
	})

	return useCallback((patch: PlacementPatch, onSettled?: () => void) => mutate(patch, { onSettled }), [mutate])
}
